import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { NavigationCloseButton, NavigationHelpButton } from '@eneco/flows/components';
import { FlowRoute } from '@eneco/flows/types';
import { FlowNavigation } from '@eneco/flows/utils';
import { Box, Button, Stack } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { B2BLeadFlowNavigationRendering } from '@sitecore/types/b2bLeadFlowNavigation';

import { B2BLeadFlowContext } from '../../types';

const ORIGIN_PAGE_KEY = 'b2b_lead_flow_origin_page';
const INITIAL_HISTORY_LEN_KEY = 'b2b_lead_flow_initial_history_len';

export const Navigation: FC<{ routes: FlowRoute<B2BLeadFlowContext>[] }> = ({ routes }) => {
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);

  const { b2bLeadFlowNavigation: { fields: textualData } = {} } = usePlaceholderContent<{
    b2bLeadFlowNavigation: B2BLeadFlowNavigationRendering;
  }>();

  useEffect(() => {
    // Store source page URL when arriving via hard navigation (window.location.href / <a>)
    try {
      const candidate = document.referrer;
      if (!sessionStorage.getItem(ORIGIN_PAGE_KEY) && candidate) {
        const url = new URL(candidate, window.location.href);
        if (url.origin === window.location.origin && candidate !== window.location.href) {
          sessionStorage.setItem(ORIGIN_PAGE_KEY, candidate);
        }
      }
    } catch {
      // ignore malformed URLs
    }

    // Capture history length on first entry to support going back past flow steps (SPA navigation)
    if (!sessionStorage.getItem(INITIAL_HISTORY_LEN_KEY)) {
      sessionStorage.setItem(INITIAL_HISTORY_LEN_KEY, String(window.history.length));
    }

    return () => {
      sessionStorage.removeItem(ORIGIN_PAGE_KEY);
      sessionStorage.removeItem(INITIAL_HISTORY_LEN_KEY);
    };
  }, []);

  const handleCloseConfirm = () => {
    const originPage = sessionStorage.getItem(ORIGIN_PAGE_KEY);
    const storedInitialLen = sessionStorage.getItem(INITIAL_HISTORY_LEN_KEY);
    const fallbackHref = textualData?.header?.closeRedirectLink?.value?.href;

    sessionStorage.removeItem(ORIGIN_PAGE_KEY);
    sessionStorage.removeItem(INITIAL_HISTORY_LEN_KEY);
    setIsCloseDialogOpen(false);

    if (originPage) {
      window.location.href = originPage;
      return;
    }

    // Navigate back past all in-flow steps to the source page (covers SPA navigation)
    if (storedInitialLen !== null) {
      const initialLen = Number(storedInitialLen);
      // initialLen > 1 means there was at least one page before the flow entry
      if (initialLen > 1) {
        const stepsAdded = window.history.length - initialLen;
        window.history.go(-(stepsAdded + 1));
        return;
      }
    }

    if (fallbackHref) {
      window.location.href = fallbackHref;
    }
  };

  const {
    header,
  } = textualData ?? {};
  const { closeDialog, helpDialog, previousStepText, navigationSteps, closeRedirectLink, cancelButtonText } = header ?? {};

  return (
    <FlowNavigation
      routes={routes}
      backLinkLabel={previousStepText?.value ?? ''}
      progressIndicatorLabels={navigationSteps?.value?.enum || []}>
      <Stack direction="row" gap="1">
        {helpDialog?.value?.title || helpDialog?.value?.content ? (
          <TrackedDialog
            title={helpDialog?.value?.title}
            trigger={<NavigationHelpButton label={helpDialog?.value?.triggerText ?? ''} />}>
            <Box paddingBottom="8">
              <RichText html={helpDialog?.value?.content} />
            </Box>
          </TrackedDialog>
        ) : null}

        {closeDialog?.value?.title || closeDialog?.value?.content ? (
          <NavigationCloseButton
            label={closeDialog?.value?.triggerText ?? ''}
            onClick={() => setIsCloseDialogOpen(true)}
          />
        ) : null}
      </Stack>

      <TrackedDialog isOpen={isCloseDialogOpen} setOpen={setIsCloseDialogOpen} title={closeDialog?.value?.title}>
        <Stack direction="column" gap="2">
          <Box paddingBottom="8">
            <RichText html={closeDialog?.value?.content} />
          </Box>
          <Stack direction={{ initial: 'column', lg: 'row' }} gap="2" wrap>
            {closeRedirectLink?.value?.href ? (
              <Button onClick={handleCloseConfirm}>{closeRedirectLink?.value?.text}</Button>
            ) : null}
            {cancelButtonText?.value ? (
              <TrackedDialog.Close asChild>
                <Button action="secondary">{cancelButtonText?.value}</Button>
              </TrackedDialog.Close>
            ) : null}
          </Stack>
        </Stack>
      </TrackedDialog>
    </FlowNavigation>
  );
};
